/**
 * Run History Page - Unified view of all agent runs with analytics
 * Displays text agent runs and voice calls in a single timeline
 * Includes analytics dashboard with charts and stats
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart as RechartsPie, Pie, Cell, Legend
} from 'recharts';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  ArrowsClockwise,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChartBar,
  ChartPie,
  ChatText,
  CheckCircle,
  CircleNotch,
  Clock,
  ClockCounterClockwise,
  Eye,
  Lightning,
  Phone,
  Pulse,
  TrendUp,
  XCircle
} from '@phosphor-icons/react';
// Analytics colors
const COLORS = {
  primary: '#6366F1',
  secondary: '#8B5CF6',
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444',
  text: '#3B82F6',
  voice: '#A855F7',
  muted: '#6B7280'
};

const PIE_COLORS = [COLORS.text, COLORS.voice];

// Error Boundary for Charts - prevents chart errors from crashing the page
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.warn('Chart rendering error caught:', error?.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center text-gray-500 text-sm">
          Chart unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

// Safe Chart Container - Only renders chart when container has valid dimensions
const SafeChartContainer = ({ children, height = 192, minWidth = 150 }) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Delay chart rendering to ensure DOM is fully ready
    const timeout = setTimeout(() => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 50 && rect.height > 50) {
          setIsReady(true);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, []);

  // Also check on visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width > 50 && rect.height > 50) {
          setIsReady(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  return (
    <div 
      ref={containerRef} 
      style={{ height, minWidth, width: '100%' }}
    >
      {isReady ? (
        <ChartErrorBoundary>
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </ChartErrorBoundary>
      ) : (
        <div className="h-full flex items-center justify-center">
          <CircleNotch className="w-5 h-5 text-gray-500 animate-spin" />
        </div>
      )}
    </div>
  );
};

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, subValue, trend, color = COLORS.primary }) => (
  <div className="bg-[#111] border border-[rgba(255,255,255,0.1)] rounded-xl p-4 hover:border-[#6366F1]/30 transition-colors">
    <div className="flex items-start justify-between">
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
      {trend && (
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          trend > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="mt-3">
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400 mt-0.5">{label}</p>
      {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
    </div>
  </div>
);

// Custom Tooltip for Area Chart
const CustomAreaTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 shadow-xl">
        <p className="text-gray-300 text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Legend formatter for pie chart
const legendFormatter = (value) => <span className="text-gray-400 text-xs">{value}</span>;

// Analytics Panel Component
const AnalyticsPanel = ({ analytics, loading }) => {
  const [expanded, setExpanded] = useState(true);
  const [chartsReady, setChartsReady] = useState(false);
  
  // Delay charts rendering to ensure page is stable
  useEffect(() => {
    const timer = setTimeout(() => {
      setChartsReady(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  
  if (loading) {
    return (
      <div className="bg-[#111] border border-[rgba(255,255,255,0.1)] rounded-xl p-8 mb-6">
        <div className="flex items-center justify-center">
          <CircleNotch className="w-6 h-6 text-[#6366F1] animate-spin mr-2" />
          <span className="text-gray-400">Loading analytics...</span>
        </div>
      </div>
    );
  }
  
  if (!analytics) return null;
  
  const { overall_stats, volume_over_time, success_by_agent, type_distribution } = analytics;
  
  // Format volume data for chart - show last 14 days
  const chartData = (volume_over_time || []).slice(-14).map(d => ({
    ...d,
    date: d.date ? new Date(d.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : ''
  }));
  
  // Pie chart data
  const pieData = [
    { name: 'Text', value: type_distribution?.text || 0 },
    { name: 'Voice', value: type_distribution?.voice || 0 }
  ].filter(d => d.value > 0);
  
  // Don't render charts until ready
  const shouldRenderCharts = chartsReady && expanded;
  
  return (
    <div className="bg-[#111] border border-[rgba(255,255,255,0.1)] rounded-xl mb-6 overflow-hidden">
      {/* Header */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <ChartBar className="text-[#6366F1]" size={20} />
          <span className="text-white font-semibold">Run Analytics</span>
          <span className="text-xs text-gray-500 bg-[rgba(255,255,255,0.05)] px-2 py-0.5 rounded">
            Last {overall_stats?.period_days || 30} days
          </span>
        </div>
        {expanded ? <CaretUp size={18} className="text-gray-400" /> : <CaretDown size={18} className="text-gray-400" />}
      </button>
      
      {expanded && (
        <div className="px-6 pb-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard 
              icon={Pulse}
              label="Total Runs"
              value={overall_stats?.total_runs?.toLocaleString() || 0}
              color={COLORS.primary}
            />
            <StatCard 
              icon={CheckCircle}
              label="Success Rate"
              value={`${overall_stats?.success_rate || 0}%`}
              subValue={`${overall_stats?.completed || 0} completed`}
              color={COLORS.success}
            />
            <StatCard 
              icon={Clock}
              label="Avg Response"
              value={overall_stats?.avg_text_latency_ms ? `${(overall_stats.avg_text_latency_ms / 1000).toFixed(1)}s` : '-'}
              subValue="Text agents"
              color={COLORS.warning}
            />
            <StatCard 
              icon={Phone}
              label="Voice Calls"
              value={type_distribution?.voice || 0}
              subValue={`${type_distribution?.voice_percentage || 0}% of total`}
              color={COLORS.voice}
            />
          </div>
          
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Volume Over Time Chart */}
            <div className="lg:col-span-2 bg-[#0A0A0A] rounded-xl p-4 border border-[rgba(255,255,255,0.05)]">
              <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                <TrendUp size={14} />
                Run Volume (Last 14 Days)
              </h3>
              {shouldRenderCharts && chartData && chartData.length > 0 ? (
                <SafeChartContainer height={192} minWidth={200}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="textGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.text} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={COLORS.text} stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="voiceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.voice} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={COLORS.voice} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: '#6B7280', fontSize: 11 }}
                      axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    />
                    <YAxis 
                      tick={{ fill: '#6B7280', fontSize: 11 }}
                      axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    />
                    <Tooltip content={<CustomAreaTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="text" 
                      name="Text"
                      stackId="1"
                      stroke={COLORS.text} 
                      fill="url(#textGradient)"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="voice" 
                      name="Voice"
                      stackId="1"
                      stroke={COLORS.voice} 
                      fill="url(#voiceGradient)"
                    />
                  </AreaChart>
                </SafeChartContainer>
              ) : !shouldRenderCharts ? (
                <div className="h-48 flex items-center justify-center">
                  <CircleNotch className="w-5 h-5 text-gray-500 animate-spin" />
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
                  No volume data available
                </div>
              )}
            </div>
            
            {/* Type Distribution Pie */}
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[rgba(255,255,255,0.05)]">
              <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                <ChartPie size={14} />
                Run Type Distribution
              </h3>
              {shouldRenderCharts && pieData.length > 0 ? (
                <SafeChartContainer height={192} minWidth={150}>
                  <RechartsPie>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={65}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend 
                      verticalAlign="bottom"
                      formatter={legendFormatter}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a1a', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#fff' }}
                    />
                  </RechartsPie>
                </SafeChartContainer>
              ) : !shouldRenderCharts ? (
                <div className="h-48 flex items-center justify-center">
                  <CircleNotch className="w-5 h-5 text-gray-500 animate-spin" />
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
                  No data available
                </div>
              )}
            </div>
          </div>
          
          {/* Top Agents by Run Count */}
          {success_by_agent && success_by_agent.length > 0 && (
            <div className="mt-6 bg-[#0A0A0A] rounded-xl p-4 border border-[rgba(255,255,255,0.05)]">
              <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                <Lightning size={14} />
                Top Agents by Run Count
              </h3>
              <div className="space-y-3">
                {success_by_agent.slice(0, 5).map((agent, index) => (
                  <div key={agent.agent_id} className="flex items-center gap-3">
                    <span className="text-gray-500 text-xs w-4">{index + 1}</span>
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs ${
                      agent.run_type === 'voice' 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {agent.run_type === 'voice' ? '📞' : '🤖'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{agent.agent_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all"
                            style={{ 
                              width: `${agent.success_rate}%`,
                              backgroundColor: agent.success_rate >= 80 ? COLORS.success : agent.success_rate >= 50 ? COLORS.warning : COLORS.error
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 w-12">{agent.success_rate}%</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-white">{agent.total}</p>
                      <p className="text-xs text-gray-500">runs</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RunHistory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeOrganization } = useOrganization();
  
  // State
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  
  // Filters
  const [agentFilter, setAgentFilter] = useState(searchParams.get('agent') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  
  // Agents list for filter dropdown
  const [agents, setAgents] = useState([]);
  
  const pageSize = 20;

  // Fetch agents for filter dropdown
  useEffect(() => {
    const fetchAgents = async () => {
      if (!activeOrganization) return;
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/agents`,
          {},
          activeOrganization.organization_id
        );
        if (response.ok) {
          const data = await response.json();
          setAgents(data.agents || []);
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
      }
    };
    fetchAgents();
  }, [activeOrganization]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    if (!activeOrganization) return;
    
    try {
      setAnalyticsLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent/all/runs/analytics?days=30`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeOrganization]);

  // Fetch runs
  const fetchRuns = useCallback(async () => {
    if (!activeOrganization) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      params.append('limit', pageSize);
      params.append('offset', (currentPage - 1) * pageSize);
      
      if (agentFilter) params.append('agent_id', agentFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.append('run_type', typeFilter);
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/agent/all/runs?${params.toString()}`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs || []);
        setTotal(data.total || 0);
      } else {
        throw new Error('Failed to fetch runs');
      }
    } catch (err) {
      console.error('Error fetching runs:', err);
      setError('Failed to load run history');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization, currentPage, agentFilter, statusFilter, typeFilter]);

  useEffect(() => {
    fetchRuns();
    fetchAnalytics();
  }, [fetchRuns, fetchAnalytics]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (agentFilter) params.set('agent', agentFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (typeFilter && typeFilter !== 'all') params.set('type', typeFilter);
    if (currentPage > 1) params.set('page', currentPage);
    setSearchParams(params);
  }, [agentFilter, statusFilter, typeFilter, currentPage, setSearchParams]);

  const totalPages = Math.ceil(total / pageSize);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusStr = typeof status === 'string' ? status : (status?.status || status?.state || 'unknown');
    const statusLower = (statusStr || '').toLowerCase();
    
    if (['completed', 'success', 'answered'].includes(statusLower)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full whitespace-nowrap">
          <CheckCircle size={10} />
          Completed
        </span>
      );
    }
    if (['failed', 'error', 'busy', 'no-answer'].includes(statusLower)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full whitespace-nowrap">
          <XCircle size={10} />
          Failed
        </span>
      );
    }
    if (['running', 'in-progress', 'ringing', 'queued'].includes(statusLower)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full whitespace-nowrap">
          <CircleNotch size={10} className="animate-spin" />
          Running
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full whitespace-nowrap">
        {statusStr || 'Unknown'}
      </span>
    );
  };

  // Get type badge
  const getTypeBadge = (runType) => {
    if (runType === 'voice') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full whitespace-nowrap">
          <Phone size={10} />
          Voice
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full whitespace-nowrap">
        <ChatText size={10} />
        Text
      </span>
    );
  };

  // Clear filters
  const clearFilters = () => {
    setAgentFilter('');
    setStatusFilter('');
    setTypeFilter('all');
    setCurrentPage(1);
  };

  const hasFilters = agentFilter || statusFilter || (typeFilter && typeFilter !== 'all');

  // ArrowsClockwise all data
  const handleRefresh = () => {
    fetchRuns();
    fetchAnalytics();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <ClockCounterClockwise className="text-[#6366F1]" size={28} />
            Run History
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            View all agent runs and voice calls across your organization
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || analyticsLoading}
          className="px-4 py-2 bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-lg text-gray-300 hover:text-white hover:border-[#6366F1]/50 transition-colors flex items-center gap-2"
        >
          <ArrowsClockwise size={16} className={(loading || analyticsLoading) ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Analytics Panel - wrapped in error boundary to prevent crash */}
      <ChartErrorBoundary>
        <AnalyticsPanel analytics={analytics} loading={analyticsLoading} />
      </ChartErrorBoundary>

      {/* Filters */}
      <div className="bg-[#111] border border-[rgba(255,255,255,0.1)] rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <ChartPie size={16} className="text-gray-400" />
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
              className="bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#6366F1]"
            >
              <option value="all">All Types</option>
              <option value="text">Text Agents</option>
              <option value="voice">Voice Calls</option>
            </select>
          </div>

          {/* Agent Filter */}
          <select
            value={agentFilter}
            onChange={(e) => { setAgentFilter(e.target.value); setCurrentPage(1); }}
            className="bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#6366F1] min-w-[150px]"
          >
            <option value="">All Agents</option>
            {agents.map((agent) => (
              <option key={agent.id || agent.agent_id} value={agent.id || agent.agent_id}>
                {agent.name}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#6366F1]"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
          </select>

          {/* Clear Filters */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="text-[#6366F1] hover:text-[#818CF8] text-sm"
            >
              Clear filters
            </button>
          )}

          {/* Results count */}
          <div className="ml-auto text-sm text-gray-400">
            {total} total runs
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-[#111] border border-[rgba(255,255,255,0.1)] rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircleNotch className="w-8 h-8 text-[#6366F1] animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <XCircle className="w-12 h-12 text-red-400 mb-3" />
            <p className="text-red-400">{error}</p>
            <button onClick={fetchRuns} className="mt-3 text-[#6366F1] hover:text-[#818CF8] text-sm">
              Try again
            </button>
          </div>
        ) : runs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <ClockCounterClockwise className="w-12 h-12 text-gray-600 mb-3" />
            <p className="text-gray-400">No runs found</p>
            {hasFilters && (
              <button onClick={clearFilters} className="mt-3 text-[#6366F1] hover:text-[#818CF8] text-sm">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[rgba(255,255,255,0.02)] border-b border-[rgba(255,255,255,0.1)]">
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase px-4 py-3 w-[200px]">Agent</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase px-4 py-3 w-[80px]">Type</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase px-4 py-3">Input</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase px-4 py-3 w-[140px]">Date</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase px-4 py-3 w-[100px]">Status</th>
                  <th className="text-left text-xs font-semibold text-gray-400 uppercase px-4 py-3 w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr 
                    key={run.run_id || run.twilio_sid || run.id}
                    className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                  >
                    {/* Agent */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0 ${
                          run.run_type === 'voice' 
                            ? 'bg-purple-500/20' 
                            : 'bg-[#6366F1]/20'
                        }`}>
                          {run.run_type === 'voice' ? '📞' : '🤖'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate" style={{maxWidth: '130px'}}>
                            {typeof run.agent_name === 'string' ? run.agent_name : 'Agent'}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">
                      {getTypeBadge(run.run_type)}
                    </td>

                    {/* Input */}
                    <td className="px-4 py-3">
                      <p className="text-gray-300 text-sm truncate" style={{maxWidth: '250px'}}>
                        {typeof run.input_text === 'string' 
                          ? run.input_text 
                          : typeof run.input_summary === 'string' 
                            ? run.input_summary 
                            : run.from_number 
                              ? `Call from ${run.from_number}` 
                              : 'No input recorded'}
                      </p>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3">
                      <span className="text-gray-400 text-sm whitespace-nowrap">
                        {formatDate(run.created_at)}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      {getStatusBadge(run.status)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      {(run.session_id || run.run_id) && (
                        <button
                          onClick={() => navigate(`/partner-pro/ai-workforce/sessions/${run.session_id || run.run_id}`)}
                          className="p-1.5 text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
                          title="View Session"
                          data-testid={`view-session-${run.session_id || run.run_id}`}
                        >
                          <Eye size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-400">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#6366F1]/50 transition-colors"
            >
              <CaretLeft size={18} className="text-gray-400" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 bg-[#1a1a1a] border border-[rgba(255,255,255,0.1)] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#6366F1]/50 transition-colors"
            >
              <CaretRight size={18} className="text-gray-400" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunHistory;
